import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { TABLES } from '../../lib/constants';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPackage, FiPlus, FiInfo, FiUser, FiCamera, FiX, FiCheckCircle, FiAlertCircle, FiClock, FiAlertTriangle, FiDollarSign, FiZap } = FiIcons;

const StatusBadge = ({ status }) => {
  const styles = {
    pending_master: 'bg-blue-50 text-blue-600',
    pending_pilot: 'bg-amber-50 text-amber-600 border border-amber-100 animate-pulse',
    contested: 'bg-red-50 text-red-600',
    unpaid: 'bg-indigo-50 text-indigo-600',
    paid: 'bg-emerald-50 text-emerald-600',
    rejected: 'bg-gray-100 text-gray-400'
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter ${styles[status] || styles.pending_master}`}>
      {status.replace('_', ' ')}
    </span>
  );
};

const LogDrops = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState('');
  const [selectedItem, setSelectedItem] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (user?.master_id) {
      fetchItems();
      fetchCharacters();
      fetchRecentLogs();
      calculateMonthlyTotal();
    }
  }, [user]);

  const calculateMonthlyTotal = async () => {
    const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
    const { data } = await supabase
      .from(TABLES.LOGS)
      .select('value_at_log')
      .eq('pilot_id', user.id)
      .gte('created_at', firstDay)
      .in('status', ['paid', 'unpaid', 'pending_pilot']);

    const total = data?.reduce((sum, log) => sum + (Number(log.value_at_log) || 0), 0) || 0;
    setMonthlyTotal(total);
  };

  const fetchItems = async () => {
    const { data } = await supabase
      .from(TABLES.ITEMS)
      .select('*')
      .or(`master_id.eq.${user.master_id},master_id.is.null`)
      .order('name', { ascending: true });
    setItems(data || []);
  };

  const fetchCharacters = async () => {
    const { data } = await supabase.from(TABLES.CHARACTERS).select('*').eq('pilot_id', user.id);
    setCharacters(data || []);
  };

  const fetchRecentLogs = async () => {
    const { data } = await supabase
      .from(TABLES.LOGS)
      .select(`*, item:${TABLES.ITEMS}(name), char:${TABLES.CHARACTERS}(name)`)
      .eq('pilot_id', user.id)
      .order('created_at', { ascending: false })
      .limit(15);
    setRecentLogs(data || []);
  };

  const handleAction = async (logId, action, reason = '') => {
    setLoading(true);
    try {
      let nextStatus = action === 'accept' ? 'unpaid' : 'contested';
      
      const updatePayload = { 
        status: nextStatus,
        pilot_response: reason 
      };

      const { error } = await supabase
        .from(TABLES.LOGS)
        .update(updatePayload)
        .eq('id', logId);

      if (error) throw error;
      
      await fetchRecentLogs();
      await calculateMonthlyTotal();
      alert(`Log ${action}ed successfully!`);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedItem || !selectedChar) {
      alert("Please select both a character and an item.");
      return;
    }
    setLoading(true);
    let proofUrl = null;
    try {
      if (file) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${user.id}.${fileExt}`;
        const filePath = `drop-proofs/${user.id}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('payout-proofs')
          .upload(filePath, file);
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('payout-proofs')
          .getPublicUrl(filePath);
        proofUrl = publicUrl;
      }

      const item = items.find(i => i.id === selectedItem);
      if (!item) throw new Error("Selected item not found in catalog.");
      const qty = parseInt(quantity) || 1;
      const value = (item.rate || 0) * qty;
      const cap = user.monthly_cap || 0;
      const isExempt = !user.is_base_pay_only && cap > 0 && (monthlyTotal + value) > cap;

      const { error } = await supabase.from(TABLES.LOGS).insert([{
        pilot_id: user.id,
        master_id: user.master_id,
        item_id: selectedItem,
        character_id: selectedChar,
        quantity: qty,
        value_at_log: user.is_base_pay_only ? 0 : value,
        status: 'pending_master',
        proof_url: proofUrl,
        is_cap_exemption_request: isExempt
      }]);

      if (error) throw error;
      alert('Success! Drop entry submitted.');
      setQuantity(1);
      setSelectedItem('');
      setFile(null);
      setPreview(null);
      fetchRecentLogs();
      calculateMonthlyTotal();
    } catch (err) {
      alert(`Failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const isCapReached = !user.is_base_pay_only && (user.monthly_cap > 0) && (monthlyTotal >= user.monthly_cap);
  const totalBaseSalary = characters.reduce((sum, char) => sum + (Number(char.base_salary) || 0), 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 pb-20">
      <div className="space-y-8">
        {user.is_base_pay_only ? (
          <div className="bento-card !p-6 border-2 border-amber-100 bg-amber-50/30 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center shadow-xl">
                <SafeIcon icon={FiZap} className="text-2xl" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Fixed Earning Model</p>
                <h4 className="text-xl font-black text-gray-900">Monthly Base Salary</h4>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-black text-amber-600">₱{totalBaseSalary.toLocaleString()}</p>
            </div>
          </div>
        ) : (
          <div className={`bento-card !p-6 border-2 flex items-center justify-between ${isCapReached ? 'border-amber-200 bg-amber-50/30' : 'border-emerald-100'}`}>
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isCapReached ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white shadow-xl'}`}>
                <SafeIcon icon={isCapReached ? FiAlertTriangle : FiDollarSign} className="text-2xl" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Monthly Performance</p>
                <h4 className="text-xl font-black text-gray-900">
                  ₱{monthlyTotal.toLocaleString()} / <span className="text-blue-600">₱{user.monthly_cap?.toLocaleString()}</span>
                </h4>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm">
          <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
            <SafeIcon icon={FiPackage} className="text-indigo-600" /> Log Drop
          </h2>
          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Character</label>
              <div className="grid grid-cols-2 gap-3">
                {characters.map(char => (
                  <button 
                    key={char.id} 
                    type="button" 
                    onClick={() => setSelectedChar(char.id)} 
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${selectedChar === char.id ? 'border-indigo-600 bg-indigo-50' : 'border-gray-100 hover:border-gray-200'}`}
                  >
                    <div className={`font-black ${selectedChar === char.id ? 'text-indigo-600' : 'text-gray-900'}`}>{char.name}</div>
                    <div className="text-[9px] font-bold text-gray-400 uppercase">{char.class}</div>
                  </button>
                ))}
              </div>
              {characters.length === 0 && <p className="text-[10px] text-red-500 font-bold uppercase ml-2">No characters found.</p>}
            </div>

            <div className="space-y-4">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Item</label>
              <select className="apple-input cursor-pointer" value={selectedItem} onChange={e => setSelectedItem(e.target.value)} required>
                <option value="">Choose item...</option>
                {items.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.master_id ? '' : '[GLOBAL] '} {i.name} {!user.is_base_pay_only && `(₱${i.rate})`}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Quantity</label>
                <input type="number" className="apple-input font-black" value={quantity} onChange={e => setQuantity(e.target.value)} min="1" required />
              </div>
              <div className="space-y-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-2">Proof</label>
                <div className="relative">
                  <input type="file" id="drop-proof" accept="image/*" onChange={(e) => {
                    const f = e.target.files[0];
                    if (f) { setFile(f); setPreview(URL.createObjectURL(f)); }
                  }} className="hidden" />
                  <label htmlFor="drop-proof" className={`flex items-center justify-center h-[60px] cursor-pointer rounded-2xl border-2 border-dashed transition-all ${preview ? 'border-emerald-500 bg-emerald-50 text-emerald-600' : 'border-gray-200 hover:border-indigo-400 text-gray-400'}`}>
                    <SafeIcon icon={FiCamera} className="mr-2" />
                    <span className="text-[10px] font-black uppercase text-center">{preview ? 'Attached' : 'Upload Image'}</span>
                  </label>
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading || characters.length === 0} className="apple-button-primary w-full !py-5 text-lg shadow-2xl flex flex-col items-center justify-center">
              {loading ? <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" /> : <span>Submit Drop</span>}
            </button>
          </form>
        </div>
      </div>

      <div className="bg-white p-10 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col overflow-hidden">
        <h2 className="text-3xl font-black text-gray-900 mb-8 shrink-0">Activity</h2>
        <div className="flex-1 space-y-6 overflow-y-auto no-scrollbar pr-2">
          {recentLogs.map(log => (
            <div key={log.id} className="p-6 bg-gray-50 rounded-3xl border border-transparent hover:border-gray-200 transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm relative overflow-hidden">
                    {log.proof_url ? <img src={log.proof_url} className="w-full h-full object-cover" /> : log.char?.name?.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900">{log.item?.name} <span className="text-gray-400 text-xs">x{log.quantity}</span></h4>
                    <p className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{log.char?.name}</p>
                  </div>
                </div>
                <div className="text-right">
                  {!user.is_base_pay_only && <p className="text-lg font-black text-emerald-600">₱{log.value_at_log?.toLocaleString()}</p>}
                  <StatusBadge status={log.status} />
                </div>
              </div>
              {log.status === 'pending_pilot' && !user.is_base_pay_only && (
                <div className="mt-4 p-5 bg-white rounded-2xl border border-blue-100 flex gap-2">
                  <button type="button" onClick={() => handleAction(log.id, 'accept')} className="flex-1 py-3 bg-emerald-600 text-white rounded-xl text-[10px] font-black uppercase">Accept</button>
                  <button type="button" onClick={() => { const r = prompt("Reason for contesting?"); if (r) handleAction(log.id, 'contest', r); }} className="flex-1 py-3 bg-amber-500 text-white rounded-xl text-[10px] font-black uppercase">Contest</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LogDrops;